/**
 * Parametric shapes → collision solids in the object's local frame (+Y up,
 * forward −Z). This is the only geometry generator: gameplay physics, gameplay
 * rendering and the editor view are all built from its output (via compile).
 *
 * Seam rules (GEOMETRY.md):
 *  - Concave joints (inner side of a curve) overlap below the walking surface,
 *    so the surface has no gaps.
 *  - Convex joints meet exactly at the surface line; any extension there would
 *    stick up as a lip.
 *  - Default segment counts keep the angle between neighbouring facets ≤ 10°,
 *    the Phase 3 contact-continuity limit, so speed is carried across seams.
 */
import { arcWall, concaveCurve, ramp as rampBuilder } from '../world/LevelBuilders';
import type { SolidDef, SolidShape, Vec3Tuple } from '../world/LevelData';
import { param, type TrackObject } from './TrackDocument';
import { quatAxisAngle, quatFromEulerDeg, quatMul, rotateVec, type Quat } from './transform';

export interface LocalSolid {
  shape: SolidShape;
  position: Vec3Tuple;
  rotation: Quat;
}

export interface ShapeResult {
  solids: LocalSolid[];
  warnings: string[];
}

const ID: Quat = { x: 0, y: 0, z: 0, w: 1 };
const X: Vec3Tuple = [1, 0, 0];
const Y: Vec3Tuple = [0, 1, 0];
const Z: Vec3Tuple = [0, 0, 1];
const RAD = 180 / Math.PI;

const box = (half: Vec3Tuple, position: Vec3Tuple, rotation: Quat = ID): LocalSolid => ({ shape: { type: 'box', halfExtents: half }, position, rotation });
const fromDefs = (defs: readonly SolidDef[]): LocalSolid[] => defs.map((d) => ({ shape: d.shape, position: d.position, rotation: quatFromEulerDeg(d.rotationDeg) }));
const add = (a: Vec3Tuple, b: Vec3Tuple): Vec3Tuple => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const scale = (a: Vec3Tuple, k: number): Vec3Tuple => [a[0] * k, a[1] * k, a[2] * k];

/**
 * Warns when neighbouring facets differ by the seam-carry limit or more.
 *
 * Measured (tests/sim/compiledTrack.test.ts): a 20 m/s quarter-pipe peaks within
 * 0.3 % of the 72-segment height at 9° facets and finer, but loses 10 % at exactly
 * 10°, where contact continuity no longer carries the seam. So warn from 10°.
 */
function facetWarning(name: string, stepDeg: number): string[] {
  const step = Math.abs(stepDeg);
  return step >= 10 - 1e-6 ? [`${name}: facets turn ${step.toFixed(1)}° per segment; at 10° or more the seams cost speed (raise Segments)`] : [];
}

/** Stacked cylinder pieces so no piece exceeds the collider cap. */
function cylinder(radius: number, height: number): LocalSolid[] {
  const pieces = Math.max(1, Math.ceil(height / 2 / 8));
  const hh = height / pieces / 2;
  return Array.from({ length: pieces }, (_, i) => ({ shape: { type: 'cylinder', radius, halfHeight: hh } as const, position: [0, hh * (2 * i + 1), 0] as Vec3Tuple, rotation: ID }));
}

/**
 * A walkway following a sampled centre line. Each segment's top surface runs
 * from one sample to the next. Segments are lengthened by w/2·tan(Δyaw/2) at
 * each end so the outside of a turn has no gap; where pitch also changes,
 * that extension leaves a step of at most ext·|Δpitch| (GEOMETRY.md).
 */
function walkway(points: readonly Vec3Tuple[], width: number, thickness: number): LocalSolid[] {
  const out: LocalSolid[] = [];
  const yaw = (a: Vec3Tuple, b: Vec3Tuple): number => Math.atan2(-(b[0] - a[0]), -(b[2] - a[2]));
  const n = points.length - 1;
  const yaws = Array.from({ length: n }, (_, i) => yaw(points[i]!, points[i + 1]!));
  const turn = (i: number): number => {
    if (i < 0 || i >= n - 1) return 0;
    let d = yaws[i + 1]! - yaws[i]!;
    d -= 2 * Math.PI * Math.round(d / (2 * Math.PI));
    return Math.abs(d);
  };
  for (let i = 0; i < n; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const dz = b[2] - a[2];
    const horiz = Math.hypot(dx, dz);
    const len = Math.hypot(horiz, dy);
    if (len < 1e-6) continue;
    const pitch = Math.atan2(dy, horiz) * RAD;
    const q = quatMul(quatAxisAngle(Y, yaws[i]! * RAD), quatAxisAngle(X, pitch));
    const extBack = (width / 2) * Math.tan(turn(i - 1) / 2);
    const extFront = (width / 2) * Math.tan(turn(i) / 2);
    const half = len / 2 + (extBack + extFront) / 2;
    // Shift the centre so the extensions land on the correct ends.
    const forward = rotateVec(q, [0, 0, -1]);
    const mid = add(scale(add(a, b), 0.5), scale(forward, (extFront - extBack) / 2));
    const normal = rotateVec(q, [0, 1, 0]);
    out.push(box([width / 2, thickness / 2, half], add(mid, scale(normal, -thickness / 2)), q));
  }
  return out;
}

/** Points on a left-turning (positive angle) arc starting at the origin heading −Z. */
function arcPoints(radius: number, angleDeg: number, rise: number, segments: number): Vec3Tuple[] {
  const sign = Math.sign(angleDeg) || 1;
  const span = Math.abs(angleDeg) / RAD;
  return Array.from({ length: segments + 1 }, (_, i) => {
    const t = i / segments;
    const th = span * t;
    return [sign * (-radius + radius * Math.cos(th)), rise * t, -radius * Math.sin(th)] as Vec3Tuple;
  });
}

/** Centripetal Catmull–Rom through `pts`, `samples` segments per span (end points duplicated). */
export function catmullRom(pts: readonly Vec3Tuple[], samples: number): Vec3Tuple[] {
  if (pts.length < 2) return [...pts];
  const P = [pts[0]!, ...pts, pts[pts.length - 1]!];
  const out: Vec3Tuple[] = [pts[0]!];
  const tj = (ti: number, a: Vec3Tuple, b: Vec3Tuple): number => ti + Math.max(1e-6, Math.sqrt(Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2])));
  const lerp = (a: Vec3Tuple, b: Vec3Tuple, ta: number, tb: number, t: number): Vec3Tuple => {
    const k = (t - ta) / (tb - ta);
    return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k];
  };
  for (let s = 1; s < P.length - 2; s++) {
    const [p0, p1, p2, p3] = [P[s - 1]!, P[s]!, P[s + 1]!, P[s + 2]!];
    const t0 = 0;
    const t1 = tj(t0, p0, p1);
    const t2 = tj(t1, p1, p2);
    const t3 = tj(t2, p2, p3);
    for (let k = 1; k <= samples; k++) {
      const t = t1 + ((t2 - t1) * k) / samples;
      const a1 = lerp(p0, p1, t0, t1, t);
      const a2 = lerp(p1, p2, t1, t2, t);
      const a3 = lerp(p2, p3, t2, t3, t);
      const b1 = lerp(a1, a2, t0, t2, t);
      const b2 = lerp(a2, a3, t1, t3, t);
      out.push(lerp(b1, b2, t1, t2, t));
    }
  }
  return out;
}

function roundTunnel(radius: number, length: number, thickness: number, segments: number): LocalSolid[] {
  const step = (2 * Math.PI) / segments;
  const halfLen = radius * Math.tan(step / 2) + thickness / 2;
  return Array.from({ length: segments }, (_, k) => {
    const a = step * k;
    const r = radius + thickness / 2;
    // a = 0 is the floor; the inner face normal points at the axis.
    return box([halfLen, thickness / 2, length / 2], [Math.sin(a) * r, radius - Math.cos(a) * r, 0], quatAxisAngle(Z, a * RAD));
  });
}

export function generateShape(obj: TrackObject): ShapeResult {
  const p = <T extends number | boolean | string | readonly Vec3Tuple[]>(k: string): T => param<T>(obj, k);
  const warnings: string[] = [];
  const name = `${obj.type} "${obj.name}"`;
  switch (obj.type) {
    case 'box':
      return { solids: [box([p<number>('sizeX') / 2, p<number>('sizeY') / 2, p<number>('sizeZ') / 2], [0, 0, 0])], warnings };
    case 'platform': {
      const t = p<number>('thickness');
      return { solids: [box([p<number>('sizeX') / 2, t / 2, p<number>('sizeZ') / 2], [0, -t / 2, 0])], warnings };
    }
    case 'pillar':
    case 'cylinder':
      return { solids: cylinder(p('radius'), p('height')), warnings };
    case 'beam':
      return { solids: [box([p<number>('length') / 2, p<number>('height') / 2, p<number>('width') / 2], [0, 0, 0])], warnings };
    case 'wedge': {
      const h: Vec3Tuple = [p<number>('width') / 2, p<number>('height') / 2, p<number>('length') / 2];
      return { solids: [{ shape: { type: 'wedge', halfExtents: h }, position: [0, h[1], 0], rotation: ID }], warnings };
    }
    case 'ramp':
      return { solids: fromDefs(rampBuilder('r', [0, 0, 0], [0, p<number>('rise'), -p<number>('length')], p('width'), p('thickness'), 'x')), warnings };
    case 'curvedRamp':
    case 'arc': {
      const segments = p<number>('segments');
      const angle = p<number>('angle');
      warnings.push(...facetWarning(name, angle / segments));
      const rise = obj.type === 'arc' ? 0 : p<number>('rise');
      return { solids: walkway(arcPoints(p('radius'), angle, rise, segments), p('width'), p('thickness')), warnings };
    }
    case 'concaveCurve':
    case 'quarterPipe': {
      const r = p<number>('radius');
      const angle = obj.type === 'quarterPipe' ? 90 : p<number>('angle');
      const segments = p<number>('segments');
      warnings.push(...facetWarning(name, angle / segments));
      return { solids: fromDefs(concaveCurve('c', [0, r, 0], r, 0, angle, segments, p('width'), p('thickness'), 'x')), warnings };
    }
    case 'halfPipe': {
      const r = p<number>('radius');
      const flat = p<number>('flatWidth');
      const len = p<number>('length');
      const t = p<number>('thickness');
      const segments = p<number>('segments');
      warnings.push(...facetWarning(name, 90 / segments));
      const solids = [
        ...fromDefs(concaveCurve('a', [0, r, -flat / 2], r, 0, 90, segments, len, t, 'x')),
        ...fromDefs(concaveCurve('b', [0, r, flat / 2], r, 0, -90, segments, len, t, 'x')),
      ];
      if (flat > 0) solids.push(box([len / 2, t / 2, flat / 2], [0, -t / 2, 0]));
      return { solids, warnings };
    }
    case 'convexCurve': {
      const r = p<number>('radius');
      const angle = p<number>('angle');
      const segments = p<number>('segments');
      const t = p<number>('thickness');
      warnings.push(...facetWarning(name, angle / segments));
      const step = angle / RAD / segments;
      // Tangent facets meet exactly at the polygon vertices: no overlap on a convex surface.
      const halfLen = r * Math.tan(step / 2);
      const solids = Array.from({ length: segments }, (_, i) => {
        const a = -angle / RAD / 2 + step * (i + 0.5);
        const n: Vec3Tuple = [0, Math.cos(a), -Math.sin(a)];
        return box([p<number>('width') / 2, t / 2, halfLen], add([0, -r, 0], scale(n, r - t / 2)), quatAxisAngle(X, -a * RAD));
      });
      return { solids, warnings };
    }
    case 'curvedWall': {
      const segments = p<number>('segments');
      const angle = p<number>('angle');
      warnings.push(...facetWarning(name, angle / segments));
      return { solids: fromDefs(arcWall('w', [0, 0, 0], p('radius'), 0, angle, segments, p('height'), p('thickness'), 'x')), warnings };
    }
    case 'tunnel': {
      const len = p<number>('length');
      const w = p<number>('width');
      const h = p<number>('height');
      const t = p<number>('wallThickness');
      if (p<string>('profile') === 'round') {
        const segments = p<number>('segments');
        warnings.push(...facetWarning(name, 360 / segments));
        if (Math.abs(w - h) > 1e-9) warnings.push(`${name}: a round tunnel uses the inner width as its diameter; the height is ignored`);
        return { solids: roundTunnel(w / 2, len, t, segments), warnings };
      }
      return { solids: corridor(len, w, h, t, true), warnings };
    }
    case 'ring': {
      const segments = p<number>('segments');
      warnings.push(...facetWarning(name, 360 / segments));
      return { solids: roundTunnel(p('radius'), p('depth'), p('thickness'), segments), warnings };
    }
    case 'corridor':
      return { solids: corridor(p('length'), p('width'), p('height'), p('wallThickness'), p('ceiling')), warnings };
    case 'path': {
      const pts = catmullRom(p<readonly Vec3Tuple[]>('points'), p('samples'));
      return { solids: walkway(pts, p('width'), p('thickness')), warnings };
    }
    default:
      return { solids: [], warnings };
  }
}

function corridor(len: number, w: number, h: number, t: number, ceiling: boolean): LocalSolid[] {
  const solids = [
    box([w / 2 + t, t / 2, len / 2], [0, -t / 2, 0]),
    box([t / 2, h / 2, len / 2], [-(w / 2 + t / 2), h / 2, 0]),
    box([t / 2, h / 2, len / 2], [w / 2 + t / 2, h / 2, 0]),
  ];
  if (ceiling) solids.push(box([w / 2 + t, t / 2, len / 2], [0, h + t / 2, 0]));
  return solids;
}
